import { Col, DatePicker, Row, Select, Skeleton, Tabs } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import EqButton from '../eqButton/EqButton'
import EqCollapsePanel from '../eqCollapsePanel/EqCollapsePanel';
import "./EqHorseTaskBoard.scss"
import moment from 'moment';
import { EqHorseTrainingType, eqLoadingState, eqNotificationTypes, EqTaskTypes } from '../../shared/enums';
import Api from '../../shared/AApi';
import { ApiEndpoints, GetEventMessage, openNotificationWithIcon } from '../../shared/Common';
import { useTranslation } from "react-i18next";
import { Empty } from 'antd-v5';
import EqHorsesTaskList from '../eqHorsesTaskList/EqHorsesTaskList';
import TabPane from 'antd/lib/tabs/TabPane';
import RevealList from '../RevealList/RevealList';
import EqHorseBoard from '../eqHorseBoard/EqHorseBoard';
import { EditOrCreateHorseActivityProps, HorseActivityForm, IHorseActivityFormValues } from '../horseActivityForm/HorseActivityForm';
import EqHorseStats from '../eqHorseStats/EqHorseStats';
import { useLocation } from 'react-router-dom';
import { EqHorseGroupDto, EqUiHorse, EqUiHorseTask, EqUiHorseTraining } from '../../shared/interfaces/horse/IHorses';
import { IOptionPair } from '../../shared/interfaces/schoolTraining/IGetSchoolTrainingDto';

export interface ActivityFilterState {
    selectedActivityType: number | null;
    selectedDate: moment.Moment | null;
    selectedStatus: number | null;
    selectedHorse: string[] | string | null;
    activityTypes: { key: number; value: string }[];
    statusTypes: { key: number; value: string }[];
    horses: { key: string; value: string }[];
    horsesGroup: EqHorseGroupDto[];
}

export interface FormDataState {
    horseId: string;
    taskDate: moment.Moment;
    taskTime: moment.Moment;
    timeTo: moment.Moment;
    notificationTime:number;
    taskType: typeof EqTaskTypes;
    trainingType: typeof EqHorseTrainingType;
    duration: number;
    description: string;
}

export interface HorseActivity {
    id: string;
    key: number|string;
    activityKey: number| string;
    activityId: string;
    name: string;
    imageUrl: string;
    task: string;
    activityType: number;
    time: number;
    duration: string | number;
    notificationTime: number;
    status: number;
    isEditing: boolean;
        description?: string;  // Added as optional

}

export interface HorsesDataList {
    allData: HorseActivity[];
    filteredData: HorseActivity[];
    apiData: EqUiHorse[];
}

const EqHorseTaskBoard = () => {
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const taskId = queryParams.get('id');
    
    const [highlightedTaskId, setHighlightedTaskId] = useState<string | null>(null);
    const taskRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

    const [loading, setLoading] = React.useState<eqLoadingState>(eqLoadingState.initial);
    const [tabKey, setTabKey] = React.useState("1");
    const { t, i18n } = useTranslation();
    const [message, setMessage] = React.useState("");
    const [addTask, setAddTask] = useState(false);
    const [selectedOption, setSelectedOption] = useState(1);
    const [horsesDataListFormatted, setHorsesDataListFormatted] = useState<HorsesDataList>({
        allData: [],
        filteredData: [],
        apiData: []
    });

    const activityFilterInitialState: ActivityFilterState = {
        selectedActivityType: null,
        selectedDate: null,
        selectedStatus: null,
        selectedHorse: null,
        activityTypes: [
            { key: 1, value: t('task') },
            { key: 2, value: t('training') },
        ],
        statusTypes: [
            { key: 0, value: t('notDone') },
            { key: 1, value: t('done') },
        ],
        horses: [],
        horsesGroup: []
    };

    const initialState: FormDataState = {
        horseId: "",
        taskDate: moment(),
        taskTime: moment(),
        timeTo: moment(),
        notificationTime: 0,
        taskType: EqTaskTypes,
        trainingType: EqHorseTrainingType,
        duration: 1,
        description: ""
    };

    const [formData, setFormData] = React.useState<FormDataState>(initialState);
    const [horsesList, setHorsesList] = React.useState<IOptionPair[]>([]);
    const [horseGroup, setHorseGroup] = React.useState<EqHorseGroupDto[]>([]);
    const [activityFilter, setActivityFilter] = React.useState<ActivityFilterState>(activityFilterInitialState);
    const [isCheckAll, setIsCheckAll] = useState(false);
    const [isCollapseAll, setIsCollapseAll] = useState(false);

    const checkAll = () => {
        setIsCheckAll(!isCheckAll);
    };

    const handleIsCollapseAll = (props: boolean) => {
        setIsCollapseAll(props);
    };

    const collapseAll = () => {
        handleIsCollapseAll(!isCollapseAll);
    };

    const getQueryParams = () => {
        const searchParams = new URLSearchParams(location.search);
        const activityId = searchParams.get('activityId');
        const type = searchParams.get('type');
        const date = searchParams.get('date');
        const horseId = searchParams.get('horseId');
        return { activityId, type, date, horseId };
    };

    useEffect(() => {
        setEventDefaults();
        getHorseList();
        getHorseGroup();
    }, []);

    useEffect(() => {
        if (taskId) {
            setHighlightedTaskId(taskId);

            const timeoutId = setTimeout(() => {
                const taskElement = taskRefs.current[taskId];
                if (taskElement) {
                    taskElement.scrollIntoView({ behavior: "smooth", block: "center" });
                    taskElement.style.border = "2px solid #1890ff";
                    taskElement.style.transition = "border 0.5s ease";

                    setTimeout(() => {
                        taskElement.style.border = "none";
                        setHighlightedTaskId(null);
                    }, 3000);
                }
            }, 100);

            return () => clearTimeout(timeoutId);
        }
    }, [taskId, horsesDataListFormatted.filteredData]);

    useEffect(() => {
        const unique: { key: string; value: string }[] = [];
        if (horsesDataListFormatted.apiData && horsesDataListFormatted.apiData.length) {
            horsesDataListFormatted.apiData.forEach((d) => {
                const flt = unique.filter(s => s.key === d.id);
                if (flt.length === 0) {
                    unique.push({
                        key: d.id,
                        value: d.name,
                    });
                }
            });
            setAddHorseActivityProps(prev => ({ ...prev, horsesList: unique }));
        }
    }, [horsesDataListFormatted.apiData]);

    useEffect(() => {
        if (horsesDataListFormatted.apiData?.length) {
            const uniqueHorses = horsesDataListFormatted.apiData.reduce((acc: { key: string; value: string }[], d) => {
                if (!acc.some(s => s.key === d.id)) {
                    acc.push({
                        key: d.id,
                        value: t(d.name),
                    });
                }
                return acc;
            }, []);

            const { horseId, date } = getQueryParams();
            const parsedDate = date ? moment(Number(date)) : null;

            setActivityFilter(prevFilter => {
                const horsesChanged = prevFilter.horses.length !== uniqueHorses.length;
                const horsesGroupChanged = prevFilter.horsesGroup.length !== horseGroup.length;
                const selectedHorseChanged = prevFilter.selectedHorse !== (horseId || prevFilter.selectedHorse);
                const selectedDateChanged = parsedDate && parsedDate.isValid() && !parsedDate.isSame(prevFilter.selectedDate);

                const shouldUpdate = horsesChanged || horsesGroupChanged || selectedHorseChanged || selectedDateChanged;

                if (shouldUpdate) {
                    return {
                        ...prevFilter,
                        horses: uniqueHorses,
                        horsesGroup: horseGroup,
                        selectedHorse: horseId || prevFilter.selectedHorse,
                        selectedDate: parsedDate && parsedDate.isValid() ? parsedDate : prevFilter.selectedDate,
                    };
                }
                return prevFilter;
            });
        }
    }, [horsesDataListFormatted.apiData, horseGroup, t]);

    useEffect(() => {
        const { type, date, horseId } = getQueryParams();
        
        setActivityFilter(prevFilter => ({
            ...prevFilter,
            selectedActivityType: type ? parseInt(type) : prevFilter.selectedActivityType,
            selectedDate: date ? moment(date) : prevFilter.selectedDate,
            selectedHorse: horseId || prevFilter.selectedHorse,
        }));
    }, [location.search]);

    useEffect(() => {
        if (horsesDataListFormatted.apiData?.length) {
            if (activityFilter.selectedActivityType !== null || 
                activityFilter.selectedStatus !== null || 
                activityFilter.selectedHorse !== null || 
                activityFilter.selectedDate !== null) {
                applyfilters(
                    activityFilter.selectedDate,
                    activityFilter.selectedActivityType,
                    activityFilter.selectedStatus,
                    activityFilter.selectedHorse
                );
            }
        }
    }, [activityFilter, horsesDataListFormatted.apiData]);

    const handleDateChange = (value: moment.Moment | null) => {
        const date = value ? moment(value) : null;
        setActivityFilter(prev => ({
            ...prev, 
            selectedDate: date
        }));
        applyfilters(date, activityFilter.selectedActivityType, activityFilter.selectedStatus, activityFilter.selectedHorse);
    };

    const handleActivityTypeChange = (value: number) => {
        setActivityFilter(prev => ({
            ...prev, 
            selectedActivityType: value
        }));
        applyfilters(activityFilter.selectedDate, value, activityFilter.selectedStatus, activityFilter.selectedHorse);
    };

    const handleStatusChange = (value: number) => {
        setActivityFilter(prev => ({
            ...prev, 
            selectedStatus: value
        }));
        applyfilters(activityFilter.selectedDate, activityFilter.selectedActivityType, value, activityFilter.selectedHorse);
    };

    const handleHorseChange = (value: string | string[]) => {
        let horseArray: string[] | null = [];
    
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                horseArray = Array.isArray(parsed) ? parsed : [parsed];
            } catch {
                horseArray = [value];
            }
        } else if (Array.isArray(value)) {
            horseArray = value;
        } else if (value === undefined) {
            horseArray = null;
        } else {
            horseArray = [value as string];
        }
    
        setActivityFilter(prev => ({
            ...prev, 
            selectedHorse: horseArray
        }));
    
        applyfilters(activityFilter.selectedDate, activityFilter.selectedActivityType, activityFilter.selectedStatus, horseArray);
    };

    const getHorseList = () => {
        setLoading(eqLoadingState.loading);
        
        Api.get(ApiEndpoints.horse.getAllHorsesIncluded).then(
            (response) => {
                setLoading(eqLoadingState.loadingCompleted);
                if (response && response.status === 200) {
                    let data:EqUiHorse[]=response.data
                    sortTasks(data);
                    const formattedData = formatHorseListWithActivities([...data]);
                    const sortedData = formattedData.sort((a, b) => b.time - a.time);
                    
                    setHorsesDataListFormatted({ 
                        allData: [...sortedData], 
                        filteredData: [...sortedData], 
                        apiData: [...response.data] 
                    });

                    const horses:IOptionPair[] = data.map(item => ({
                        key: item.id,
                        value: item.name
                    }));
                    setHorsesList(horses);
                } else {
                    openNotificationWithIcon(t('update'), t('horseTaskErrorTxt'), eqNotificationTypes.error);
                }
            },
            (error) => {
                setLoading(eqLoadingState.loadingCompleted);
                openNotificationWithIcon(t('update'), t('horseTaskErrorTxt'), eqNotificationTypes.error);
            }
        );
    };


    const isTraining = (ht: EqUiHorseTask | EqUiHorseTraining): ht is EqUiHorseTraining => {
        return (ht as EqUiHorseTraining).trainingType !== undefined;
    };
    const formatHorseListWithActivities = (data: EqUiHorse[]): HorseActivity[] => {
        const output: HorseActivity[] = [];
        data.forEach(hrs => {
            hrs.horseTasks.forEach(ht => {
                output.push(mapActivity(hrs, 1, ht));
            });

            hrs.horseTrainings.forEach(ht => {
                output.push(mapActivity(hrs, 2, ht));
            });
        });
        return output;
    };

    const mapActivity = (hrs: EqUiHorse, activityType: number, ht: EqUiHorseTask | EqUiHorseTraining): HorseActivity => {
        return {
            id: hrs.id,
            key: hrs.key || '',
            activityKey: ht.key || '',
            activityId: ht.id,
            name: hrs.name,
            imageUrl: hrs.imageUrl || '',
            task: isTraining(ht) 
            ? String(EqHorseTrainingType[ht.trainingType]) 
            : String(EqTaskTypes[ht.taskType]),  // Explicitly cast to string
            activityType,
            time: ht.time,
            duration: isTraining(ht) ? ht.durationInMinutes : "",
            notificationTime: ht.notificationTime,
            status: Number(ht.status) || 0,
            isEditing: false
        };
    };

    const sortTasks = (data: EqUiHorse[]) => {
        if (!data || !data.length) return;

        data.forEach(item => {
            const currentDate = moment();
            const comingSunday = currentDate.clone().startOf('week').add(1, 'week').day(0);
            const sundayAfterComingSunday = comingSunday.clone().add(1, 'week');
            sortNow(item.horseTasks, sundayAfterComingSunday, currentDate);
            sortNow(item.horseTrainings, sundayAfterComingSunday, currentDate);
        });
    };

    const sortNow = (arr: EqUiHorseTraining[] | EqUiHorseTask[], sundayAfterComingSunday: moment.Moment, currentDate: moment.Moment) => {
        arr.sort((a, b) => {
            const dateA = moment(a.date);
            const dateB = moment(b.date);

            const isWithinNextSunday = (date: moment.Moment) => date.isBetween(currentDate, sundayAfterComingSunday, null, '[]');
            const aIsWithinNextSunday = isWithinNextSunday(dateA);
            const bIsWithinNextSunday = isWithinNextSunday(dateB);

            if (aIsWithinNextSunday && !bIsWithinNextSunday) return -1;
            if (!aIsWithinNextSunday && bIsWithinNextSunday) return 1;
            if (aIsWithinNextSunday && bIsWithinNextSunday) return dateA.diff(dateB);

            if (dateA.isAfter(currentDate) && dateB.isBefore(currentDate)) return -1;
            if (dateA.isBefore(currentDate) && dateB.isAfter(currentDate)) return 1;
            if (dateA.isAfter(currentDate) && dateB.isAfter(currentDate)) return dateA.diff(dateB);
            if (dateA.isBefore(currentDate) && dateB.isBefore(currentDate)) return dateB.diff(dateA);
            
            return 0;
        });
    };

    interface EqHorsesTaskListProps {
        horsesTaskList: HorsesDataList;
        delFunc: (val: HorseActivity) => void;
        editFunc: (val: HorseActivity) => void;
        selectedFunc: (val: HorseActivity) => void;
        openNotificationWithIcon: (title: string, message: string, type: eqNotificationTypes) => void;
    }

    const eqHorsesTaskListProps: EqHorsesTaskListProps = {
        horsesTaskList: horsesDataListFormatted,
        delFunc: (val: HorseActivity) => {
      

            if (val.activityType === 1) {

                const objToSend = {
                    key: val.activityKey,
                    date: val.time,
                    time: val.time,
                    description: "",
                    notificationTime: val.notificationTime,
                    horseId: val.id,
                    taskType : EqTaskTypes[val.task as keyof typeof EqTaskTypes]
                };
                Api.post(ApiEndpoints.horse.task.delete, objToSend).then(() => {
                    removeItemOnDelete(val);
                    openNotificationWithIcon(t('update'), t('horseTaskDeleteTxt'), eqNotificationTypes.success);
                }, error => {
                    openNotificationWithIcon(t('update'), t('horseTaskErrorTxt'), eqNotificationTypes.error);
                });
            } else {
                const objToSend = {
                    key: val.activityKey,
                    date: val.time,
                    time: val.time,
                    description: "",
                    notificationTime: val.notificationTime,
                    horseId: val.id,
                    trainingType : EqHorseTrainingType[val.task as keyof typeof EqHorseTrainingType],
                    durationInMinutes: val.duration !== "" ? Number(val.duration) : 0
                };
                // objToSend.trainingType = EqHorseTrainingType[val.task as keyof typeof EqHorseTrainingType];
                // objToSend.durationInMinutes = val.duration !== "" ? Number(val.duration) : 0;

                Api.post(ApiEndpoints.horse.training.delete, objToSend).then(() => {
                    removeItemOnDelete(val);
                    openNotificationWithIcon(t('update'), t('horseTrainingDeleteTxt'), eqNotificationTypes.success);
                }, error => {
                    openNotificationWithIcon(t('update'), t('horseTaskErrorTxt'), eqNotificationTypes.error);
                });
            }
        },
        editFunc: (val: HorseActivity) => {
            console.log(horsesDataListFormatted, "horsesDataListFormatted");
        },
        selectedFunc: (val: HorseActivity) => {
            // Handle selection
        },
        openNotificationWithIcon
    };

    const removeItemOnDelete = (val: HorseActivity) => {
        const updatedList = { ...horsesDataListFormatted };

        let index = updatedList.allData.findIndex(d => d.activityKey === val.activityKey);
        if (index !== -1) updatedList.allData.splice(index, 1);

        index = updatedList.filteredData.findIndex(d => d.activityKey === val.activityKey);
        if (index !== -1) updatedList.filteredData.splice(index, 1);
        
        setHorsesDataListFormatted(updatedList);
    };

    const setEventDefaults = () => {
        setFormData({ ...initialState });
    };

    const [addHorseActivityProps, setAddHorseActivityProps] = useState<EditOrCreateHorseActivityProps>({
        horsesList: [],
        cancelFunc: (item, val) => {
            setAddTask(!val);
        },
        createResultFunc: (isSuccess: boolean, addedActivityData?: EqUiHorseTask, message?: string) => {
            if (isSuccess) {
                getHorseList();
                openNotificationWithIcon(t('update'), message as string, eqNotificationTypes.success);
            } else {
                openNotificationWithIcon(t('update'), message as string, eqNotificationTypes.error);
            }
        },
        formValues: {
            horseId: "",
            taskDate: moment(),
            taskTime: moment(),
            timeTo: moment(),
            notificationTime: undefined,
            taskType: EqTaskTypes.Deworming,
            trainingType: EqHorseTrainingType.Dressage,
            duration: 1,
            description: ""
        }
    });

    // const handleSaveTask = () => {
    //     const tim = moment(formData.taskTime);
    //     const dat = moment(formData.taskDate);
    //     dat.set("hour", tim.get('hour'));
    //     dat.set("minute", tim.get('minute'));
        
    //     const dataToSend = {
    //         horseId: formData.horseId,
    //         time: moment(dat).valueOf(),
    //         date: moment(dat).valueOf(),
    //         notificationTime: CalcNotificationTime(formData.notificationTime),
    //         description: formData.description
    //     };

    //     if (selectedOption === 1) {
    //         dataToSend.taskType = formData.taskType;
    //         Api.post(ApiEndpoints.horse.task.add, dataToSend).then(() => {
    //             setEventDefaults();
    //             openNotificationWithIcon(t('update'), t('horseTaskAddTxt'), eqNotificationTypes.success);
    //         }, error => {
    //             openNotificationWithIcon(t('update'), t('horseTaskErrorTxt'), eqNotificationTypes.error);
    //         });
    //     } else {
    //         dataToSend.trainingType = formData.trainingType;
    //         dataToSend.durationInMinutes = CalcDurationInMinutes(formData.duration);
    //         Api.post(ApiEndpoints.horse.training.add, dataToSend).then(() => {
    //             setEventDefaults();
    //             openNotificationWithIcon(t('update'), t('horseTrainingAddTxt'), eqNotificationTypes.success);
    //         }, error => {
    //             openNotificationWithIcon(t('update'), t('horseTaskErrorTxt'), eqNotificationTypes.error);
    //         });
    //     }
    // };

    // const CalcDurationInMinutes = (param: number): number => {
    //     switch (param) {
    //         case 1: return 20;
    //         case 2: return 30;
    //         case 3: return 60;
    //         case 4: return 120;
    //         default: return 30;
    //     }
    // };

    // const CalcNotificationTime = (param: number): number => {
    //     switch (param) {
    //         case 1: return moment().add(6, 'w').valueOf();
    //         case 2: return moment().add(8, 'w').valueOf();
    //         case 3: return moment().add(3, 'M').valueOf();
    //         case 4: return moment().add(8, 'M').valueOf();
    //         case 5: return moment().add(1, 'y').valueOf();
    //         default: return moment().add(8, 'w').valueOf();
    //     }
    // };

    // const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    //     setSelectedOption(Number(e.target.value));
    // };

    const onChangeTab = (key: string) => {
        clearFilters();
        setTabKey(key);
    };

    const clearFilters = () => {
        setActivityFilter(prev => ({
            ...prev,
            selectedActivityType: null,
            selectedDate: null,
            selectedStatus: null,
            selectedHorse: null,
        }));
        setHorsesDataListFormatted(prev => ({ ...prev, filteredData: prev.allData }));
    };

    const applyfilters = (
        date: moment.Moment | null,
        activityType: number | null,
        status: number | null,
        horse: string[] | string | null
    ) => {
        const statusValid = status !== null && !isNaN(status);
        let filtered: HorseActivity[] = [];
        let initialFiltered = horsesDataListFormatted.allData || [];
        
        if (date) {
            const selectedDate = moment(date).startOf('day');
            initialFiltered = initialFiltered.filter(d => 
                moment(d.time).isSame(selectedDate, 'day')
            );
        }
        
        if (activityType && statusValid) {
            initialFiltered = initialFiltered.filter(d => 
                d.activityType === activityType && d.status === status
            );
        } else if (activityType) {
            initialFiltered = initialFiltered.filter(d => 
                d.activityType === activityType
            );
        } else if (statusValid) {
            initialFiltered = initialFiltered.filter(d => 
                d.status === status
            );
        }
        
        if (horse) {
            const horseIds = Array.isArray(horse) ? horse : [horse];
            filtered = initialFiltered.filter(d => horseIds.includes(d.id));
        } else {
            filtered = initialFiltered;
        }
        
        setHorsesDataListFormatted(prev => ({ ...prev, filteredData: filtered }));
    };

    const prepareAddTask = (val: boolean) => {
        var firstValue = horsesDataListFormatted.filteredData[0];
        setAddHorseActivityProps({ ...addHorseActivityProps});
        setAddTask(val);
    }

    // const prepareAddTask = (val: boolean) => {
    //     const firstValue = horsesDataListFormatted.filteredData[0];
    //     const formValues: IHorseActivityFormValues = {
    //         horseId: firstValue.id,
    //         taskDate: moment(firstValue.time),
    //         taskTime: moment(firstValue.time),
    //         timeTo: moment(firstValue.time),
    //         notificationTime: firstValue.notificationTime,
    //         taskType: firstValue.activityType === 1 
    //             ? EqTaskTypes[firstValue.task as keyof typeof EqTaskTypes ]
    //             : undefined,
    //         trainingType: firstValue.activityType === 2 
    //             ? EqHorseTrainingType[firstValue.task as keyof typeof EqHorseTrainingType] 
    //             : undefined,
    //         duration: typeof firstValue.duration === 'string' 
    //             ? parseInt(firstValue.duration) || 0 
    //             : firstValue.duration,
    //         description: firstValue.description || ""
    //     };
        
    //     setAddHorseActivityProps(prev => ({ 
    //         ...prev, 
    //         formValues 
    //     }));
    //     setAddTask(val);
    // };

    // const removeHorseActivity = (val) => {
    //     // Implementation if needed
    // };

    // const deleteActivityCancel = (val) => {
    //     // Implementation if needed
    // };

    const getHorseGroup = () => {
        Api.get(ApiEndpoints.GetHorseGroups).then(
            (response) => {
                if (response && response.status === 200) {
                    
                    setMessage("");
                    const data = response.data;
                    setHorseGroup(data);
                    setActivityFilter(prev => ({ ...prev, horsesGroup: data }));
                    
                    // if (data) {
                    //     data.forEach((obj) => {
                    //         obj.selected = false;
                    //     });
                    // }
                } else {
                    setMessage(GetEventMessage(response.status));
                }
            },
            (error) => {
                setMessage(GetEventMessage(error.response.status));
            }
        );
    };

    return (
        <div className="horse-management">
            <Tabs activeKey={tabKey} onChange={onChangeTab}>
                <TabPane tab={t("horseActivities")} key="1">
                    <div className="loading"><Skeleton loading={loading === eqLoadingState.loading} active></Skeleton></div>
                    <div className="list">
                        <RevealList>
                            <Col className="eq-HorseTask-container">
                                <div className="add-task">
                                    <EqButton
                                        type="primary"
                                        className=""
                                        onClick={() => prepareAddTask(!addTask)}
                                    >
                                        +{t('addActivity')}
                                    </EqButton>
                                </div>
                                {addTask && (
                                    <EqCollapsePanel
                                        defaultOpen={true}
                                        header={t("horseTask")}
                                        children={
                                            <HorseActivityForm {...addHorseActivityProps} />
                                        }
                                    />
                                )}

                                <Row>
                                    <Col span={24} className="filters">
                                        <Row gutter={[16, 16]} className="filter-type">
                                            <Col className="location-col " xs={24} sm={24} md={12} lg={11}>
                                                <Row className="location-selector">
                                                    <Col xs={6} sm={5} md={6} lg={5}>
                                                        <label className="picker-label">{t("horse")}</label>
                                                    </Col>
                                                    <Col className="location-input" flex="auto">
                                                        <Select
                                                            className="eq-filter-radius eq-input-info"
                                                            allowClear
                                                            placeholder={t("selectHorse")}
                                                            onChange={handleHorseChange}
                                                            value={
                                                                Array.isArray(activityFilter.selectedHorse) 
                                                                    ? activityFilter.horsesGroup.find(group => 
                                                                        JSON.stringify(group?.horses?.map((horse) => horse.id)) === 
                                                                        JSON.stringify(activityFilter.selectedHorse)
                                                                      )?.name 
                                                                    : activityFilter.horses.find(horse => horse.key === activityFilter.selectedHorse)?.value
                                                            }
                                                        >
                                                            {activityFilter.horses?.map(actvty => (
                                                                <Select.Option
                                                                    label={actvty.value}
                                                                    value={actvty.key}
                                                                    key={actvty.key}
                                                                >
                                                                    {actvty.value}
                                                                </Select.Option>
                                                            ))}
                                                            {activityFilter.horsesGroup?.map(group => (
                                                                <Select.Option
                                                                    label={group.name}
                                                                    value={JSON.stringify(group?.horses?.map((horse) => horse.id))}
                                                                    key={group.key}
                                                                >
                                                                    {group.name}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col className="location-col " xs={24} sm={24} md={12} lg={11}>
                                                <Row className="location-selector">
                                                    <Col xs={6} sm={5} md={6} lg={5}>
                                                        <label className="picker-label">{t("activityType")}</label>
                                                    </Col>
                                                    <Col className="location-input" flex="auto">
                                                        <Select
                                                            className="eq-filter-radius eq-input-info"
                                                            allowClear
                                                            placeholder={t("selectType")}
                                                            onChange={handleActivityTypeChange}
                                                            value={activityFilter.selectedActivityType}
                                                        >
                                                            {activityFilter.activityTypes.map(actvty => (
                                                                <Select.Option
                                                                    label={actvty.value}
                                                                    value={actvty.key}
                                                                    key={actvty.key}
                                                                >
                                                                    {actvty.value}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col className="radius-col" xs={24} sm={24} md={12} lg={11}>
                                                <Row className="radius-selector">
                                                    <Col xs={6} sm={5} md={6} lg={5}>
                                                        <label className="picker-label">{t("status")}</label>
                                                    </Col>
                                                    <Col className="radius-input" flex="auto">
                                                        <Select
                                                            className="eq-filter-radius  eq-input-info"
                                                            allowClear
                                                            placeholder={t("status")}
                                                            onChange={handleStatusChange}
                                                            value={activityFilter.selectedStatus}
                                                        >
                                                            {activityFilter.statusTypes.map(mark => (
                                                                <Select.Option label={t(mark.value)} value={mark.key} key={mark.key}>
                                                                    {t(mark.value)}
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    </Col>
                                                </Row>
                                            </Col>

                                            <Col className="date-col " xs={24} sm={24} md={12} lg={11}>
                                                <Row className="date-selector  start-date-selector">
                                                    <Col xs={6} sm={5} md={6} lg={5}>
                                                        <label className="picker-label">{t("date")}</label>
                                                    </Col>
                                                    <Col className="date-input" flex="auto">
                                                        <DatePicker
                                                            placeholder={t("selectDate")}
                                                            className="eq-filter-radius  eq-input-info"
                                                            value={activityFilter.selectedDate}
                                                            onChange={handleDateChange}
                                                        />
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col className="date-col " xs={24} sm={24} md={12} lg={11}>
                                                &nbsp;
                                            </Col>

                                            <Col className="search-col" xs={24} sm={24} md={12} lg={11}>
                                                <Row className="date-selector  start-date-selector">
                                                    <Col xs={6} sm={5} md={6} lg={5}>
                                                        <label className="picker-label">&nbsp;</label>
                                                    </Col>
                                                    <Col className="date-input" flex="auto">
                                                        <div className="search">
                                                            <EqButton onClick={clearFilters}>
                                                                {t("clearFilters")}
                                                            </EqButton>
                                                        </div>
                                                    </Col>
                                                </Row>
                                            </Col>
                                        </Row>
                                    </Col>
                                </Row>

                                {loading === eqLoadingState.loadingCompleted && horsesDataListFormatted.filteredData?.length ? (
                                    <Col span={24}>
                                        <Row className="eq-fl-row hidden-sm">
                                            <Col className="eq-fl-th" span={5}>
                                                {t("name")}
                                            </Col>
                                            <Col className="eq-fl-th" span={3}>
                                                {t("activity")}
                                            </Col>
                                            <Col className="eq-fl-th" span={3}>
                                                {t("time")}
                                            </Col>
                                            <Col className="eq-fl-th" span={3}>
                                                {t("duration")}
                                            </Col>
                                            <Col className="eq-fl-th" span={2}>
                                                {t("type")}
                                            </Col>
                                            <Col className="eq-fl-th" span={3}>
                                                {t("notification")}
                                            </Col>
                                            <Col className="eq-fl-th" span={3}>
                                                {t("status")}
                                            </Col>
                                            <Col className="eq-fl-th" span={2}>
                                                {t("actions")}
                                            </Col>
                                        </Row>
                                        <div className="hidden-md">
                                            <Row justify="start" align="middle" className="eq-list-checkAll">
                                                <Col>
                                                    <EqButton
                                                        classNames="btn-background-orange"
                                                        onClick={collapseAll}
                                                    >
                                                        {isCollapseAll ? "Collapse All" : "Expand All"}
                                                    </EqButton>
                                                </Col>
                                            </Row>
                                        </div>
                                        <EqHorsesTaskList {...eqHorsesTaskListProps} />
                                    </Col>
                                ) : (
                                    loading === eqLoadingState.loadingCompleted && !horsesDataListFormatted.filteredData?.length && (
                                        <div className="no-data">
                                            <Empty description={t("noHorseActivityAvailable")}></Empty>
                                        </div>
                                    )
                                )}
                            </Col>
                        </RevealList>
                    </div>
                </TabPane>
                <TabPane tab={t("horseProfiles")} key="2">
                    <RevealList>
                        <EqHorseBoard />
                    </RevealList>
                </TabPane>
                <TabPane tab={t("horseStats")} key="3">
                    {tabKey === '3' && (
                        <RevealList>
                            <EqHorseStats />
                        </RevealList>
                    )}
                </TabPane>
            </Tabs>
        </div>
    );
};

export default EqHorseTaskBoard;